import type { FetchQueryOptions } from '@zg-rentals/react-query5';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type AuthConfig<Props extends {}, User extends {}> = {
  /**
   * Return React Query options for fetching the user. The caller controls
   * every aspect of the query (queryKey, queryFn, staleTime, retry, etc.).
   * `queryKey` is required so the same key can be reused by `prefetch` and
   * the client `Provider`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryOptions: (props: Props) => FetchQueryOptions<User, Error, User, any>;

  /**
   * Determine if user is authorized (after the user is fetched). Default
   * behavior is to always return true.
   */
  isAuthorized: (user: User, props: Props) => boolean;
};

/**
 * Define an auth config once, then pair it with `defineClient` for the
 * client runtime and `prefetch` from `@zg-rentals/auth-react/server` for
 * server-side prefetching.
 *
 * @example
 * ```ts
 * // auth.ts (server-safe — no React imports)
 * export const authConfig = defineConfig({
 *   queryOptions: ({ httpClient }: { httpClient: Client }) => ({
 *     queryKey: ['auth'],
 *     queryFn: () => fetchUser(httpClient),
 *   }),
 *   isAuthorized: (user) => user.loggedIn,
 * });
 *
 * // AuthProvider.tsx
 * import { defineClient } from '@zg-rentals/auth-react/client';
 * export const Auth = defineClient(authConfig);
 *
 * // app/page.tsx (RSC)
 * import { prefetch } from '@zg-rentals/auth-react/server';
 * const result = await prefetch(authConfig, queryClient, { httpClient });
 * ```
 */
export function defineConfig<Props extends {}, User extends {}>(
  config: AuthConfig<Props, User>,
): AuthConfig<Props, User> {
  return config;
}
